import {useState, useLayoutEffect, useCallback, RefObject, CSSProperties} from "react"

const DEFAULT_POPOVER_HEIGHT = 305
const DEFAULT_POPOVER_WIDTH = 225
const DEFAULT_GAP = 4

type PositionOptions = {
    popoverHeight?: number
    popoverWidth?: number
    gap?: number
    zIndex?: number
}

type HookOptions = PositionOptions & {
    isOpen: boolean
    anchorRef: RefObject<HTMLElement>
}

export function getAutoPopoverPosition(
    anchor: HTMLElement,
    {
        popoverHeight = DEFAULT_POPOVER_HEIGHT,
        popoverWidth = DEFAULT_POPOVER_WIDTH,
        gap = DEFAULT_GAP,
        zIndex = 1050
    }: PositionOptions = {}
): CSSProperties {
    const rect = anchor.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const openUp = spaceBelow < popoverHeight && spaceAbove > spaceBelow

    const spaceRight = window.innerWidth - rect.left
    const spaceLeft = rect.right
    const openLeft = spaceRight < popoverWidth && spaceLeft > spaceRight

    return {
        position: "fixed",
        zIndex,
        ...(openLeft ? {right: window.innerWidth - rect.right, left: "auto"} : {left: rect.left, right: "auto"}),
        ...(openUp
            ? {bottom: window.innerHeight - rect.top + gap, top: "auto"}
            : {top: rect.bottom + gap, bottom: "auto"})
    }
}

export function useAutoPopoverPosition({
    isOpen,
    anchorRef,
    popoverHeight = DEFAULT_POPOVER_HEIGHT,
    popoverWidth = DEFAULT_POPOVER_WIDTH,
    gap = DEFAULT_GAP,
    zIndex = 1050
}: HookOptions): {style: CSSProperties; updatePosition: () => void} {
    const [style, setStyle] = useState<CSSProperties>({})

    const updatePosition = useCallback(() => {
        const anchor = anchorRef.current
        if (!anchor) return
        setStyle(getAutoPopoverPosition(anchor, {popoverHeight, popoverWidth, gap, zIndex}))
    }, [anchorRef, popoverHeight, popoverWidth, gap, zIndex])

    useLayoutEffect(() => {
        if (!isOpen) return

        updatePosition()

        window.addEventListener("resize", updatePosition)
        window.addEventListener("scroll", updatePosition, true)

        return () => {
            window.removeEventListener("resize", updatePosition)
            window.removeEventListener("scroll", updatePosition, true)
        }
    }, [isOpen, updatePosition])

    return {style, updatePosition}
}
